import type { ReactNode } from "react";
import { MutationError, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  RestrictedState,
  StatCard,
  StatusLabel,
  Table,
  TableCard,
  type StatusTone,
} from "../../ui";
import { useT } from "../../i18n/index";
import { BarChart, Donut, Sparkline } from "../reports/charts";
import type { BarDatum } from "../reports/charts";
import type {
  AdminPlatformResponse,
  Compliance,
  ConfigGovernance,
  CurationCoverage,
  CustomerFunnel,
  OperationalHealth,
  PilotScorecard,
} from "../../api/admin-platform-contracts";
import { useAdminPlatform, useExportAdminPlatform } from "./use-dashboard";

// 04 — Yönetim Paneli (platform "Panel"). ENRICHED to consume the ONE composed platform
// read (#204, GET /v1/metrics/admin-platform): curation coverage, config governance, the
// customer-#1 funnel, KVKK/compliance, operational health, and the pilot scorecard
// (R1–R6). Calm, chartjunk-free, auditable, honest-empty: a source with no rows renders a
// real zero / empty series, a genuinely absent surface (DSAR) renders a dignified "no
// source" note (never a zero pretending to be data), and the external-feed-pending cost
// denominators (CAC, advisor cost) stay labelled — never faked. Charts are the shared
// dependency-free k-kit primitives (../reports/charts); NO chart library, NO new palette.

/** A stored 0–1 rate as a percentage for display. */
function pct01(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/** A number-or-em-dash: a null value is "no data to measure", shown as "—". */
function orDash(v: number | string | null | undefined): ReactNode {
  return v == null ? "—" : String(v);
}

/** Money is a decimal STRING (numeric(14,2)/TRY) — formatted in ink, tabular, grouped;
 *  NEVER alarm-red. Falls back to the raw string if it is not a finite number. */
const TRY_FMT = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  minimumFractionDigits: 2,
});
function money(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? TRY_FMT.format(n) : v;
}

/** The panel's "as of" — the whole rollup's generatedAt in the reader's locale. */
function asOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

/** A from/to echo for the context chips (ISO in, an "all-time" label when both blank). */
function rangeLabel(from: string | null, to: string | null, allLabel: string): string {
  if (!from && !to) return allLabel;
  return `${from ?? "…"} → ${to ?? "…"}`;
}

const STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: "active",
  DRAFT: "pending",
  PENDING: "review",
  SUPERSEDED: "ended",
  RETIRED: "ended",
};

export function Dashboard() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canRead = hasGrant(me, "analytics:read", "all");
  const panel = useAdminPlatform({}, canRead);
  const exportCsv = useExportAdminPlatform();
  const data = panel.data;
  const pending = data?.configGovernance.pendingApprovals ?? null;

  return (
    <section className="dash pan">
      <div className="dash-headrow">
        <div>
          <h1 className="dash-title">{t("dash.heading")}</h1>
          {data ? (
            <p className="dash-greeting muted">
              {t("dash.greetingPre")}{" "}
              {pending && pending > 0 ? (
                <>
                  <strong>
                    {pending} {t("dash.greetingApprovals")}
                  </strong>{" "}
                  {t("dash.greetingPost")}
                </>
              ) : (
                t("dash.greetingNone")
              )}
            </p>
          ) : (
            <p className="dash-greeting muted">{t("pan.subtitle")}</p>
          )}
        </div>
        {data ? (
          <div className="rep-period" data-testid="pan-context">
            <span className="rep-period-chip">
              <Icon name="calendar" size={14} strokeWidth={1.9} />
              {t("pan.asOf")}: {asOf(data.generatedAt)}
            </span>
            <span className="rep-period-chip">
              <Icon name="measure" size={14} strokeWidth={1.9} />
              {t("pan.cohort")}: {rangeLabel(data.cohort.from, data.cohort.to, t("pan.periodAll"))}
            </span>
            <span className="rep-period-chip">
              <Icon name="measure" size={14} strokeWidth={1.9} />
              {t("pan.window")}: {rangeLabel(data.window.from, data.window.to, t("pan.periodAll"))}
            </span>
            {/* A12 — export the AGGREGATE rollup as CSV. Only these numbers: no
                customer, household or policy row leaves through here. Rendered
                only once `data` exists, so the button never offers a file for a
                panel that has not loaded. */}
            <button
              type="button"
              className="k-btn k-btn--soft k-btn--sm"
              onClick={() => exportCsv.mutate({})}
              disabled={exportCsv.isPending}
              data-testid="pan-export"
            >
              <Icon name="download" size={14} strokeWidth={1.9} />
              {t("pan.export")}
            </button>
            {/* A 403 or 500 stopped the spinner and produced neither a file nor a
                message, so the operator re-clicked indefinitely. */}
            <MutationError
              error={exportCsv.error}
              fallback={t("pan.exportFailed")}
              offline={t("common.offline")}
            />
          </div>
        ) : null}
      </div>

      {!canRead && (
        <RestrictedState
          data-testid="pan-restricted"
          title={t("dash.restricted")}
          description="analytics:read @ all"
        />
      )}
      {canRead && panel.isLoading && (
        <LoadingState data-testid="pan-loading" label={t("pan.loading")} rows={4} />
      )}
      {canRead && panel.error != null && (
        <ErrorState
          data-testid="pan-error"
          title={t("pan.error")}
          description={panel.error instanceof ProblemError ? panel.error.problem.detail : undefined}
        />
      )}
      {canRead && data != null && <PanelBody data={data} />}
    </section>
  );
}

function PanelBody({ data }: { data: AdminPlatformResponse }) {
  return (
    <div className="pan-grid">
      <CurationCard cov={data.curationCoverage} />
      <ConfigGovernanceCard gov={data.configGovernance} />
      <FunnelCard funnel={data.customerFunnel} />
      <ComplianceCard compliance={data.compliance} />
      <OperationalHealthCard health={data.operationalHealth} />
      <PilotScorecardCard scorecard={data.pilotScorecard} />
    </div>
  );
}

// ── (1) Curation kapsamı ────────────────────────────────────────────────────────
function CurationCard({ cov }: { cov: CurationCoverage }) {
  const { t } = useT();
  const rate = cov.total > 0 ? cov.curated / cov.total : 0;
  return (
    <Card className="pan-card" data-testid="pan-curation">
      <div className="pan-card-head">
        <span className="pan-card-title">{t("pan.cur.title")}</span>
      </div>
      {cov.total === 0 ? (
        <EmptyState data-testid="pan-curation-empty" icon="quality" title={t("pan.cur.empty")} />
      ) : (
        <div className="pan-cov">
          <Donut
            value={rate}
            caption={t("pan.cur.curated")}
            tone="blue"
            valueTestId="pan-curation-pct"
            ariaLabel={t("pan.cur.title")}
          />
          <ul className="rep-stats">
            <li>
              {t("pan.cur.curated")}: <strong>{cov.curated}</strong>
            </li>
            <li>
              {t("pan.cur.total")}: <strong>{cov.total}</strong>
            </li>
            <li>
              {t("pan.cur.coldStart")}: <strong>{cov.coldStart}</strong>
            </li>
          </ul>
        </div>
      )}
      <p className="muted rep-desc">{t("pan.cur.note")}</p>
    </Card>
  );
}

// ── (2) Config governance ─────────────────────────────────────────────────────────
function ConfigGovernanceCard({ gov }: { gov: ConfigGovernance }) {
  const { t } = useT();
  const namespaces = Object.entries(gov.activeVersionsByNamespace);
  return (
    <TableCard
      className="pan-card pan-card--wide"
      data-testid="pan-config"
      title={t("pan.cfg.title")}
    >
      <div className="pan-config-pending" data-testid="pan-config-pending">
        <span className="k-foureyes-pill">{t("dash.badge.fourEyes")}</span>
        <span>
          {t("pan.cfg.pending")}: <strong>{gov.pendingApprovals}</strong>
        </span>
      </div>

      <div className="pan-config-active">
        <span className="pan-sub-title">{t("pan.cfg.active")}</span>
        {namespaces.length === 0 ? (
          <p className="muted" data-testid="pan-config-active-empty">
            {t("pan.cfg.activeEmpty")}
          </p>
        ) : (
          <ul className="pan-namespaces">
            {namespaces.map(([ns, v]) => (
              <li key={ns} data-testid="pan-namespace-row">
                <span className="pan-ns-name">{ns}</span>
                <span className="pan-ns-ver muted">
                  v{v.version} · {t("pan.cfg.guardrail")} v{v.guardrailVersion}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pan-config-recent">
        <span className="pan-sub-title">{t("pan.cfg.recent")}</span>
        {gov.recentChanges.length === 0 ? (
          <EmptyState data-testid="pan-config-empty" icon="records" title={t("pan.cfg.empty")} />
        ) : (
          <Table className="pan-table">
            <thead>
              <tr>
                <th>{t("pan.cfg.colNamespace")}</th>
                <th>{t("pan.cfg.colVersion")}</th>
                <th>{t("pan.cfg.colStatus")}</th>
                <th>{t("pan.cfg.colGovernance")}</th>
                <th>{t("pan.cfg.colReason")}</th>
                <th className="pan-td-right">{t("pan.cfg.colAsOf")}</th>
              </tr>
            </thead>
            <tbody>
              {gov.recentChanges.map((c) => (
                <tr key={`${c.namespace}-${c.version}`} data-testid="pan-config-row">
                  <td>{c.namespace}</td>
                  <td className="pan-num">v{c.version}</td>
                  <td>
                    <StatusLabel tone={STATUS_TONE[c.status] ?? "pending"}>{c.status}</StatusLabel>
                  </td>
                  <td className="muted">{c.governanceLevel}</td>
                  <td className="muted">{orDash(c.changeReason)}</td>
                  <td className="pan-td-right muted">{asOf(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </TableCard>
  );
}

// ── (3) Customer #1 funnel ─────────────────────────────────────────────────────────
function FunnelCard({ funnel }: { funnel: CustomerFunnel }) {
  const { t } = useT();
  const bars: BarDatum[] = [
    { label: t("pan.fun.leads"), value: funnel.leads, display: funnel.leads, tone: "blue" },
    {
      label: t("pan.fun.recommendations"),
      value: funnel.recommendations,
      display: funnel.recommendations,
      tone: "blue",
    },
    {
      label: t("pan.fun.issued"),
      value: funnel.issuedPolicies,
      display: funnel.issuedPolicies,
      tone: "green",
    },
  ];
  return (
    <Card className="pan-card" data-testid="pan-funnel">
      <div className="pan-card-head">
        <span className="pan-card-title">{t("pan.fun.title")}</span>
      </div>
      <p className="muted rep-desc">{t("pan.fun.desc")}</p>
      <BarChart data={bars} ariaLabel={t("pan.fun.title")} testId="pan-funnel-bars" />
      <ul className="rep-stats pan-conv">
        <li>
          {t("pan.fun.l2r")}: <strong>{pct01(funnel.conversion.leadToRecommendation)}</strong>
        </li>
        <li>
          {t("pan.fun.r2p")}: <strong>{pct01(funnel.conversion.recommendationToPolicy)}</strong>
        </li>
        <li>
          {t("pan.fun.l2p")}: <strong>{pct01(funnel.conversion.leadToPolicy)}</strong>
        </li>
      </ul>
    </Card>
  );
}

// ── (4) KVKK / uyum ─────────────────────────────────────────────────────────────────
function ComplianceCard({ compliance }: { compliance: Compliance }) {
  const { t } = useT();
  const grants = compliance.consentTrend.map((d) => d.grants);
  const totalGrants = grants.reduce((s, v) => s + v, 0);
  const totalWithdrawals = compliance.consentTrend.reduce((s, d) => s + d.withdrawals, 0);
  const suppression: BarDatum[] = Object.entries(compliance.suppressionByReason).map(
    ([reason, n]) => ({ label: reason, value: n, display: n, tone: "amber" }),
  );
  return (
    <Card className="pan-card" data-testid="pan-compliance">
      <div className="pan-card-head">
        <span className="pan-card-title">{t("pan.kvkk.title")}</span>
      </div>

      <span className="pan-sub-title">{t("pan.kvkk.consentTrend")}</span>
      <Sparkline
        points={grants}
        tone="green"
        testId="pan-consent-spark"
        ariaLabel={t("pan.kvkk.consentTrend")}
      />
      <ul className="rep-stats pan-consent-legend">
        <li>
          {t("pan.kvkk.grants")}: <strong>{totalGrants}</strong>
        </li>
        <li>
          {t("pan.kvkk.withdrawals")}: <strong>{totalWithdrawals}</strong>
        </li>
      </ul>

      <span className="pan-sub-title">
        {t("pan.kvkk.suppression")}: <strong>{compliance.suppressionTotal}</strong>
      </span>
      {suppression.length === 0 ? (
        <p className="muted" data-testid="pan-suppression-empty">
          {t("pan.kvkk.suppressionEmpty")}
        </p>
      ) : (
        <BarChart data={suppression} ariaLabel={t("pan.kvkk.suppression")} tone="amber" />
      )}

      <div className="pan-dsar">
        <span className="pan-sub-title">{t("pan.kvkk.dsar")}</span>
        {compliance.dsarRequests.total === 0 ? (
          <p className="muted pan-note-dignified" data-testid="pan-dsar-empty">
            <Icon name="info" size={14} strokeWidth={1.9} />
            {t("pan.kvkk.dsarEmpty")}
          </p>
        ) : (
          <span className="pan-dsar-value">
            <strong data-testid="pan-dsar-value">{compliance.dsarRequests.total}</strong>
            <span className="muted">
              {" "}
              · {t("pan.kvkk.dsarOpen")} <strong>{compliance.dsarRequests.open}</strong> ·{" "}
              {t("pan.kvkk.dsarFulfilled")} <strong>{compliance.dsarRequests.fulfilled}</strong>
            </span>
          </span>
        )}
      </div>
    </Card>
  );
}

// ── (5) Operasyonel sağlık ──────────────────────────────────────────────────────────
/** A calm alert stat — red ONLY when it is a genuine problem (a positive PAID_NOT_ISSUED
 *  or SLA breach); otherwise the ink/neutral treatment (never alarm for a healthy zero). */
function AlertStat({
  label,
  value,
  alarm,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  alarm: boolean;
  testId: string;
}) {
  return (
    <div
      className={`pan-alert${alarm ? " pan-alert--alarm" : ""}`}
      data-testid={testId}
      data-alarm={alarm ? "true" : "false"}
    >
      <span className="pan-alert-icon" aria-hidden="true">
        <Icon name={alarm ? "alert-triangle" : "check-circle"} size={18} strokeWidth={1.9} />
      </span>
      <div>
        <div className="pan-alert-value">{value}</div>
        <div className="pan-alert-label">{label}</div>
      </div>
    </div>
  );
}

function OperationalHealthCard({ health }: { health: OperationalHealth }) {
  const { t } = useT();
  const byType = Object.entries(health.issuanceExceptionsByType);
  const healthy = health.paidNotIssued === 0 && health.slaBreaches === 0;
  return (
    <Card className="pan-card" data-testid="pan-ops">
      <div className="pan-card-head">
        <span className="pan-card-title">{t("pan.ops.title")}</span>
      </div>
      <div className="pan-alerts">
        <AlertStat
          testId="pan-ops-paid-not-issued"
          label={t("pan.ops.paidNotIssued")}
          value={health.paidNotIssued}
          alarm={health.paidNotIssued > 0}
        />
        <AlertStat
          testId="pan-ops-issuance-exceptions"
          label={t("pan.ops.issuanceExceptions")}
          value={health.issuanceExceptions}
          alarm={false}
        />
        <AlertStat
          testId="pan-ops-sla-breaches"
          label={t("pan.ops.slaBreaches")}
          value={health.slaBreaches}
          alarm={health.slaBreaches > 0}
        />
      </div>
      {byType.length > 0 && (
        <div className="pan-ops-bytype">
          <span className="pan-sub-title">{t("pan.ops.byType")}</span>
          <ul className="rep-stats">
            {byType.map(([type, n]) => (
              <li key={type} data-testid="pan-ops-type-row">
                {type}: <strong>{n}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}
      {healthy && (
        <p className="muted rep-desc" data-testid="pan-ops-calm">
          {t("pan.ops.calm")}
        </p>
      )}
    </Card>
  );
}

// ── (6) Pilot scorecard (R1–R6) ─────────────────────────────────────────────────────
/** A dignified "external feed pending" placeholder — for the cost denominators that live
 *  outside the platform (CAC channel-spend, advisor cost). NEVER a fabricated number. */
function ExternalFeedPending({ label }: { label: string }) {
  const { t } = useT();
  return (
    <div className="rep-external" data-testid="external-feed-pending">
      <span className="rep-external-icon" aria-hidden="true">
        <Icon name="lock" size={15} strokeWidth={1.9} />
      </span>
      <div className="rep-external-body">
        <Badge variant="warning">{t("rep.externalPending")}</Badge>
        <p className="rep-external-desc muted">{label}</p>
      </div>
    </div>
  );
}

function PilotScorecardCard({ scorecard }: { scorecard: PilotScorecard }) {
  const { t } = useT();
  return (
    <Card className="pan-card pan-card--wide" data-testid="pan-pilot">
      <div className="pan-card-head">
        <span className="pan-card-title">{t("pan.pilot.title")}</span>
      </div>
      <div className="pan-pilot-kpis" data-testid="pan-pilot-kpis">
        <StatCard
          icon="measure"
          iconTone="blue"
          value={pct01(scorecard.conversionBySource.totals.conversionRate)}
          label={t("pan.pilot.r1")}
        />
        <StatCard
          icon="health"
          iconTone="green"
          value={pct01(scorecard.motorHealthConversion.conversionRate)}
          label={t("pan.pilot.r2")}
        />
        <StatCard
          icon="household"
          iconTone="blue"
          value={orDash(scorecard.householdDepth.householdDepth.median)}
          label={t("pan.pilot.r3")}
        />
        <StatCard
          icon="protection"
          iconTone="green"
          value={pct01(scorecard.retention.retentionRate)}
          label={t("pan.pilot.r4")}
        />
        <StatCard
          icon="performance"
          iconTone="blue"
          value={money(scorecard.advisorEconomics.totals.commissionReceived)}
          label={t("pan.pilot.r5")}
        />
        <StatCard
          icon="quality"
          iconTone="green"
          value={pct01(scorecard.premiumExperience.priceIntegrity.matchRate)}
          label={t("pan.pilot.r6")}
        />
      </div>
      <div className="pan-pilot-external">
        <ExternalFeedPending label={t("pan.pilot.cacPending")} />
        <ExternalFeedPending label={t("pan.pilot.costPending")} />
      </div>
    </Card>
  );
}
